import React from "react";
import { Progress } from "@/components/ui/progress";
import { formatTime } from "@/utils/formatTime";
import { Workout, Exercise } from "@/schemas/workout";
import VoiceCommandPanel from "@/components/workout/VoiceCommandPanel";
import VoiceCommandFeedback from "@/components/workout/VoiceCommandFeedback";
import VoiceDebugPanel from "@/components/workout/VoiceDebugPanel";
import ExerciseDisplay from "@/components/workout/ExerciseDisplay";
import RestTimer from "@/components/workout/RestTimer";
import { WorkoutSettings } from "../WorkoutSettings";

interface ActiveWorkoutViewProps {
  workout: Workout | null;
  currentExerciseIndex: number;
  currentSetIndex: number;
  restMode: boolean;
  currentRestTime: number;
  workoutProgress: number;
  isLastExercise: boolean;
  isLastSet: boolean;
  totalSets: number;
  soundEnabled: boolean;
  toggleSound: () => void;
  playSound: (type: "timer" | "rest" | "countdown" | "complete") => void;
  voiceEnabled: boolean;
  toggleVoice: () => void;
  completeSet: () => void;
  skipExercise: () => void;
  skipRestCommand: () => void;
  handleRestComplete: () => void;
  resetWorkout: () => void;
  exitWorkout: () => void;
  debugMode: boolean;
  debugVolume: number;
  setDebugVolume: (volume: number) => void;
  isListening: boolean;
  transcript: string;
  voiceTranscripts: string[];
  voiceStatus: string;
  toggleListening: () => void;
  permissionDenied: boolean;
  recognizedCommand: string;
  showCommandFeedback: boolean;
  setShowCommandFeedback: (show: boolean) => void;
  commandMapping: Record<string, { phrases: string[]; action: () => void; description: string }>;
  elapsedTime: number;
  isRestAfterExercise: boolean;
  isPaused: boolean;
  pauseWorkout: () => void;
  resumeWorkout: () => void;
}

export default function ActiveWorkoutView({
  workout,
  currentExerciseIndex,
  currentSetIndex,
  restMode,
  currentRestTime,
  workoutProgress,
  isLastExercise,
  isLastSet,
  totalSets,
  soundEnabled,
  toggleSound,
  playSound,
  voiceEnabled,
  toggleVoice,
  completeSet,
  skipExercise,
  skipRestCommand,
  handleRestComplete,
  resetWorkout,
  exitWorkout,
  debugMode,
  debugVolume,
  setDebugVolume,
  isListening,
  transcript,
  voiceTranscripts,
  voiceStatus,
  toggleListening,
  permissionDenied,
  recognizedCommand,
  showCommandFeedback,
  setShowCommandFeedback,
  commandMapping,
  elapsedTime,
  isRestAfterExercise,
  isPaused,
  pauseWorkout,
  resumeWorkout,
}: ActiveWorkoutViewProps) {
  if (!workout) throw new Error("Workout is null");

  const currentExercise: Exercise = workout.exercises[currentExerciseIndex];
  const nextExerciseName =  currentExercise?.name;

  const voiceCommandPanelMemo = voiceEnabled ? (
    <VoiceCommandPanel
      isListening={isListening}
      transcript={transcript}
      voiceTranscripts={voiceTranscripts}
      voiceStatus={voiceStatus}
      toggleListening={toggleListening}
      permissionDenied={permissionDenied}
      commandMapping={commandMapping}
    />
  ) : null;

  const voiceCommandFeedbackMemo = voiceEnabled ? (
    <VoiceCommandFeedback
      command={recognizedCommand}
      isVisible={showCommandFeedback}
      onHide={() => setShowCommandFeedback(false)}
    />
  ) : null;

  const voiceDebugPanelMemo = debugMode ? (
    <VoiceDebugPanel
      isListening={isListening}
      transcript={transcript}
      voiceTranscripts={voiceTranscripts}
      voiceStatus={voiceStatus}
    />
  ) : null;

  return (
    <div className="container max-w-md mx-auto p-4">
      {/* Overlay when paused */}
      {isPaused && (
        <div className="fixed inset-0 z-40 bg-black bg-opacity-60 flex flex-col items-center justify-center">
          <div className="text-white text-3xl font-bold mb-4">Workout Paused</div>
          <button
            onClick={resumeWorkout}
            className="bg-green-500 hover:bg-green-600 text-white px-6 py-3 rounded-lg text-xl font-semibold shadow-lg"
          >
            Resume
          </button>
        </div>
      )}
      {/* Header with progress and settings */}
      <div className="mb-4 flex items-center justify-between">
        <Progress value={workoutProgress} className="h-2 flex-1 mr-2" />
        <WorkoutSettings
          soundEnabled={soundEnabled}
          toggleSound={toggleSound}
          voiceEnabled={voiceEnabled}
          toggleVoice={toggleVoice}
          volume={debugVolume}
          setVolume={setDebugVolume}
          onReset={resetWorkout}
          onExit={exitWorkout}
        />
      </div>
      {/* Voice command panel */}
      {voiceCommandPanelMemo}
      {/* Rest timer or exercise display */}
      {restMode ? (
        <RestTimer
          currentRestTime={currentRestTime}
          handleRestComplete={handleRestComplete}
          skipRestCommand={skipRestCommand}
          isLastSet={isLastSet}
          currentSetIndex={currentSetIndex}
          totalSets={totalSets}
          nextExerciseName={nextExerciseName}
          soundEnabled={soundEnabled}
          playSound={playSound}
          isRestAfterExercise={isRestAfterExercise}
          disabled={isPaused}
        />
      ) : (
        <>
          <ExerciseDisplay
            exercise={currentExercise}
            currentSetIndex={currentSetIndex}
            totalSets={totalSets}
            completeSet={completeSet}
            soundEnabled={soundEnabled}
            playSound={playSound}
            disabled={isPaused}
          />
          <div className="flex justify-between text-sm text-gray-500">
            <div>
              {currentExerciseIndex + 1}/{workout.exercises.length} exercises
            </div>
            <div>Time: {formatTime(elapsedTime)}</div>
          </div>
        </>
      )}
      {/* Debug panel */}
      {voiceDebugPanelMemo}
      {/* Voice command feedback */}
      {voiceCommandFeedbackMemo}
      {/* Fixed Pause/Resume button bottom right */}
      {!isPaused && (
        <button
          onClick={pauseWorkout}
          className="fixed bottom-6 right-6 z-50 bg-yellow-500 hover:bg-yellow-600 text-white px-5 py-3 rounded-full text-lg font-semibold shadow-lg flex items-center gap-2"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>
          Pause
        </button>
      )}
    </div>
  );
}
